/** Render pipeline for the Tier 1 layer document.
 *
 *  Two paths:
 *    1. **Fast path** — the document has no groups, no masks, and
 *       no adjustment layers. Plain Fabric rendering is correct
 *       and fastest. Tier 0 documents always hit this path, so
 *       there is zero perf impact for users not using Tier 1
 *       features. `renderDocument` returns immediately on this
 *       path; Fabric's own loop already painted the leaves.
 *    2. **Slow path** — at least one slow-path feature is present.
 *       Phase 2 handles **adjustment layers** in full; mask
 *       feather/density/inverted/disabled and non-passthrough
 *       groups continue to no-op with a dev warn until later
 *       phases ship. The adjustment compositor walks the tree
 *       back-to-front maintaining a running composite. Each
 *       rasterizable leaf is painted into the composite via the
 *       caller-supplied `paintLeaf`; each adjustment reads the
 *       composite, applies its config, writes the result to the
 *       adjustment's backing canvas (sourced via
 *       `getAdjustmentBacking`), and blits that back onto the
 *       running composite so subsequent adjustments see the
 *       cumulative effect.
 *
 *  AI tools' "flatten visible" path will use this same pipeline
 *  transparently — no AI surface changes when more slow-path
 *  features land. */

import { applyAdjustmentToCanvas, isImplementedKind } from './adjustments/dispatch';
import { traverseDescendants } from './layer-tree';
import type { EditorDocument, LayerNode } from '../types';

export interface RenderPipelineOptions {
  /** Source-of-truth layer tree. Pipeline walks this. */
  document: EditorDocument;
  /** Canvas dimensions in scene-space pixels. Used to size the
   *  running composite. Should match the editor's canvas
   *  dimensions; adjustment backing canvases are expected to match. */
  width: number;
  height: number;
  /** Paint one leaf layer's pixels (with its current transform)
   *  onto the target's 2d context. Caller knows how to read
   *  Fabric objects, paint surfaces, and apply transforms. No-op
   *  when the leaf has no rasterizable content (text/shape v1
   *  here, since the slow path doesn't visually composite vector
   *  shapes yet — they'll still render through Fabric in their
   *  normal z-order). The compositor passes the target as a
   *  full-canvas element; `paintLeaf` should NOT clear it (multiple
   *  leaves accumulate). */
  paintLeaf: (target: HTMLCanvasElement, layerId: string) => void;
  /** Look up the backing canvas the adjustment's display Fabric
   *  object wraps. The compositor writes the post-adjustment
   *  pixels here; caller is responsible for marking the wrapping
   *  Fabric object dirty + `requestRenderAll` AFTER `renderDocument`
   *  returns. Returning `null` skips that adjustment — useful when
   *  the adjustment's display object hasn't been created yet
   *  (e.g., during the first render after a new adjustment node
   *  was added). */
  getAdjustmentBacking: (layerId: string) => HTMLCanvasElement | null;
  /** Optional cache the compositor reads/writes for the per-
   *  adjustment "composite-below" optimization. Caller owns the
   *  Map (typically a `useRef` on the editor) and is responsible
   *  for clearing it when loading a new document via
   *  `clearRenderCache`. When omitted, every pass runs the full
   *  work for every adjustment. See `createCompositorCache`. */
  cache?: CompositorCache;
  /** Optional scratch buffers for the interactive proxy path.
   *  When supplied, the buffers are allocated once and reused
   *  across `renderDocument` calls — avoiding ~1.5MB of GC churn
   *  per interactive frame at 60fps. Allocate via
   *  `createProxyScratchHolder` and hold in a ref alongside the
   *  cache. When omitted, the compositor allocates fresh per-call
   *  (correct but garbage-y under sustained interaction). */
  proxyScratch?: ProxyScratchHolder;
  /** When true, the compositor uses a viewport-resolution proxy
   *  for cache-miss adjustment shader runs — the composite read
   *  is downsampled to `INTERACTIVE_PROXY_MAX_EDGE` longest-edge,
   *  the GPU shader runs at proxy resolution, and the result is
   *  upsampled into the full-resolution backing canvas. Cache
   *  hits still use the cached full-resolution backing unchanged.
   *
   *  Intended for slider-drag windows in adjustment edit mode —
   *  the modal sets this true on mouse-down, false on mouse-up.
   *  Interactive frames write proxy-resolution pixels to backings
   *  (bypassing cache writes); the next non-interactive pass
   *  cache-misses the changed adjustments and re-renders them at
   *  full resolution. Documents whose longest edge is already
   *  below the proxy threshold see no behavior change. */
  interactiveMode?: boolean;
}

/** Caller-owned cache for the per-adjustment "composite-below"
 *  optimization. Keyed by adjustment layer id; opaque entries.
 *  Initialize via `createCompositorCache` and pass into every
 *  `renderDocument` call from the same editor instance. Clear on
 *  new-document load via `clearRenderCache`. */
export type CompositorCache = Map<string, CompositorCacheEntry>;

/** Opaque cache entry. Treat as a black box — the only operation
 *  the caller performs is constructing an empty cache or clearing
 *  it. The compositor reads/writes the contents. */
export interface CompositorCacheEntry {
  /** Hash of the running composite up to and including this
   *  adjustment plus the adjustment's own config + clip target.
   *  When the recomputed fingerprint matches, the backing canvas
   *  already holds the correct output pixels and the shader run
   *  is skipped. */
  fingerprint: string;
}

/** Allocate a fresh compositor cache. Caller should hold this in
 *  a stable ref across renderDocument calls and clear it (via
 *  `clearRenderCache`) when loading a different document. */
export function createCompositorCache(): CompositorCache {
  return new Map();
}

/** Drop every cached entry. Use on session restore, new-document
 *  load, and any other path that may invalidate canvas pixel
 *  state in ways the fingerprint can't see (e.g. destructive
 *  preview ticks — see EditorPage's `handleAdjustmentPreviewTick`).
 *  Wrapper around `Map.clear()` so the cache's underlying shape
 *  stays an implementation detail. */
export function clearRenderCache(cache: CompositorCache): void {
  cache.clear();
}

/** Caller-owned scratch holder for the interactive proxy buffers.
 *  The compositor lazily allocates the two HTMLCanvasElements on
 *  first interactive cache-miss and reuses them across calls.
 *  When the canvas dimensions change, the next call detects the
 *  size mismatch and reallocates. Treat fields as opaque. */
export interface ProxyScratchHolder {
  composite: HTMLCanvasElement | null;
  result: HTMLCanvasElement | null;
}

/** Allocate an empty proxy scratch holder. Caller should hold
 *  this in a stable ref across renderDocument calls. */
export function createProxyScratchHolder(): ProxyScratchHolder {
  return { composite: null, result: null };
}

/** Per-pass stats returned by `renderDocument`. Surfaced for
 *  dev-mode logging — production builds may discard. Tests use
 *  these to assert cache behavior directly rather than inferring
 *  it from call counts. */
export interface RenderStats {
  /** Adjustments where the cache held a matching fingerprint —
   *  shader run, `getImageData`, and deferred leaf paint all
   *  skipped. */
  cacheHits: number;
  /** Adjustments where the cache had no entry, a mismatched
   *  fingerprint, or no cache was supplied — full shader work ran
   *  (at proxy resolution in interactive mode, full-res otherwise). */
  cacheMisses: number;
}

const EMPTY_RENDER_STATS: RenderStats = Object.freeze({ cacheHits: 0, cacheMisses: 0 });

/** Longest-edge cap (in scene-space pixels) used when
 *  `interactiveMode` is true. Matches the AdjustmentsModal's
 *  `MAX_PREVIEW_DIM = 768` — the modal already downscales the
 *  destructive preview source to this resolution, so running the
 *  slow-path at a higher proxy res would just upsample blurry
 *  input. Aligning the two caps keeps interactive cost minimal
 *  (~4-6ms per adjustment on 4K) without losing fidelity the user
 *  could see anyway during a drag. */
const INTERACTIVE_PROXY_MAX_EDGE = 768;

/** True if the document needs the custom compositor — any group
 *  whose blend / opacity isn't passthrough or any adjustment layer
 *  triggers the slow path. False = Tier 0-shaped document, plain
 *  Fabric rendering suffices.
 *
 *  Mask features (feather / density / invert / disable) no longer
 *  route through the slow path. They're materialized into the
 *  Fabric clipPath's effective alpha by
 *  `useFabricCanvas.materializeMaskClipPath`; Fabric's native
 *  `destination-in` clipPath compositing renders them correctly,
 *  and the slow-path compositor's `paintLeaf` calls
 *  `fabricObj.render(ctx)` which honors the clipPath, so adjustment
 *  clipping to a feathered/inverted/density-attenuated leaf also
 *  Just Works. */
export function requiresCustomComposite(doc: EditorDocument): boolean {
  for (const id of doc.rootIds) {
    if (nodeRequiresCustom(doc, id)) return true;
  }
  return false;
}

function nodeRequiresCustom(doc: EditorDocument, id: string): boolean {
  const node: LayerNode | undefined = doc.layers[id];
  if (!node) return false;
  if (node.type === 'adjustment') return true;
  if (node.type === 'group' && groupNeedsSlowPath(node)) return true;
  if (node.childIds && node.childIds.length > 0) {
    for (const childId of node.childIds) {
      if (nodeRequiresCustom(doc, childId)) return true;
    }
  }
  return false;
}

/** True when the group's blend / opacity isn't passthrough.
 *  Custom blend modes need offscreen compositing; opacity < 1
 *  needs flatten-then-fade. The UI doesn't surface group blend /
 *  opacity yet, so this always returns false in practice today. */
function groupNeedsSlowPath(node: LayerNode): boolean {
  const opacity = node.opacity ?? 1;
  const blendMode = node.blendMode ?? 'source-over';
  return opacity < 1 || blendMode !== 'source-over';
}

/** True when the document has slow-path features the compositor
 *  doesn't yet handle — currently just non-passthrough groups.
 *  Phase 2 implements adjustment layers in full; masks are now
 *  handled by `useFabricCanvas.materializeMaskClipPath` (Fabric's
 *  native clipPath alpha compositing). When non-passthrough groups
 *  land, this should drop to a hard `false`. */
export function hasUnimplementedSlowPath(doc: EditorDocument): boolean {
  for (const id in doc.layers) {
    const node = doc.layers[id];
    if (node.type === 'group' && groupNeedsSlowPath(node)) return true;
  }
  return false;
}

/** Leaf types the compositor paints into the running composite.
 *  Text and shape are excluded for Phase 2 — they render through
 *  Fabric in normal z-order, which is correct for the common case
 *  (adjustment layers above paint/image, with text/shape on top).
 *  When text/shape need to sit BELOW an adjustment, the compositor
 *  will need to rasterize them in tree order; deferred until a
 *  user asks. */
function isCompositableLeaf(node: LayerNode): boolean {
  return node.type === 'image' || node.type === 'paint';
}

/** Render the document. Fast path is a no-op (Fabric's own
 *  render loop already paints leaf layers correctly). Slow path
 *  walks the tree back-to-front, threading each rasterizable
 *  leaf through `paintLeaf` and each adjustment through
 *  `applyAdjustmentToCanvas`. Adjustment outputs are written to
 *  caller-owned backing canvases — caller must mark the
 *  wrapping Fabric objects dirty + `requestRenderAll` after this
 *  returns.
 *
 *  Adjustments marked `clipToBelow: true` are masked by the alpha
 *  of the immediately-preceding leaf in back-to-front order —
 *  Photoshop's clipping-mask semantics. Outside the clip
 *  region, the prior composite passes through unchanged.
 *
 *  No-op when slow-path features other than adjustments are
 *  present (mask feather/density/inverted/disabled, non-
 *  passthrough groups). Caller should `console.warn` once per
 *  document mutation in dev.
 *
 *  ── Performance characteristics ─────────────────────────────────
 *  Cost per pass is dominated by per-adjustment work, in this order
 *  (4K canvas / one BC adjustment ballpark):
 *   1. `getImageData` on the running composite — ~50ms (32MB CPU↔GPU
 *      transfer). Required input for the adjustment math.
 *   2. `applyAdjustmentToCanvas` per-pixel JS loop — ~100-400ms,
 *      kind-dependent. LUT-based kinds (B/C, Curves, Levels) are
 *      cheapest; HSL and Color Balance involve per-pixel HSL
 *      round-trips and run 3-4× slower.
 *   3. `putImageData` writing the result into the backing canvas —
 *      ~30ms.
 *   4. Clipping (if any) — a few drawImage calls via canvas2d
 *      composite operations; tens of ms even on 4K. Was previously
 *      a per-pixel JS blend at ~200ms; the composite-op refactor
 *      brought it under control.
 *
 *  So a single 4K adjustment costs ~200-500ms per recomposite; two
 *  stacked roughly doubles. Eye-toggle / opacity-slider / similar
 *  triggers a full recomposite (the entire adjustment stack must
 *  re-evaluate against the new visibility/transform). The rAF
 *  coalescing in the EditorPage effect prevents per-frame stacking,
 *  but each individual user action still incurs the per-adjustment
 *  cost.
 *
 *  GPU shaders (lib/gpu/*) replace the per-pixel JS loop for kinds
 *  that have a shader implementation — see
 *  `applyAdjustmentToCanvasWithGpu` in `adjustments/dispatch.ts`,
 *  which is GPU-first with JS fallback per kind. For supported
 *  kinds, what used to be 200-500ms on a 4K image is in the
 *  single-digit-ms range on the GPU. Mobile especially benefits —
 *  mobile GPUs are good, mobile JS engines are not. Unsupported
 *  kinds still run on the JS path and pay the per-pixel cost.
 *
 *  Two further perf knobs sit on top of the GPU port:
 *    (a) Per-adjustment cache (opts.cache). When supplied, each
 *        adjustment's output backing is keyed by a fingerprint of
 *        its input composite + its own config. Cache hits skip the
 *        `getImageData` + shader run AND skip painting any leaves
 *        below the hit — the cached backing already encodes them.
 *        Eye-toggle / opacity / clip-target toggles on stacks where
 *        only one adjustment changed drop from N×~90ms to a single
 *        cheap `drawImage` per cached adjustment.
 *    (b) Interactive proxy (opts.interactiveMode). When true and the
 *        canvas is larger than `INTERACTIVE_PROXY_MAX_EDGE` longest-
 *        edge, cache-miss adjustments run at viewport resolution
 *        (downsample composite → shader → upsample to backing).
 *        Live preview cost on 4K drops from ~90ms to ~10-15ms per
 *        adjustment. The backing carries upsampled-blurry pixels
 *        until the next non-interactive pass; cache entries for
 *        proxy-rendered adjustments are deleted so that next pass
 *        cache-misses them and re-renders at full resolution. */
export function renderDocument(opts: RenderPipelineOptions): RenderStats {
  if (!requiresCustomComposite(opts.document)) return EMPTY_RENDER_STATS;
  if (hasUnimplementedSlowPath(opts.document)) return EMPTY_RENDER_STATS;

  const composite = document.createElement('canvas');
  composite.width = opts.width;
  composite.height = opts.height;
  const compositeCtx = composite.getContext('2d', { willReadFrequently: true });
  if (!compositeCtx) return EMPTY_RENDER_STATS;

  let cacheHits = 0;
  let cacheMisses = 0;

  // Back-to-front order. `toOrderedList` returns front-most first;
  // we reverse for paint order. Walking the flat (depth-first
  // flattened) list directly works because passthrough groups
  // contribute no compositing of their own — their children just
  // render in their tree z-order, which is what we get from the
  // flattened walk.
  const order = backToFrontOrder(opts.document);

  // Pre-walk to resolve clip targets. Tree-aware: each clipped
  // adjustment clips to the next visible non-adjustment SIBLING in
  // front-most-first sibling order (i.e. the layer immediately
  // below it in the LayersPanel within its own scope). When that
  // sibling is a group, the clip mask is the union alpha of every
  // visible rasterizable descendant of the group — so an
  // adjustment placed above a group ends up scoped to "everything
  // inside that group", which is the user-intent fix for the
  // group-as-target case.
  //
  // Resolution returns an ORDERED list of leaf ids to paint into
  // the mask canvas (back-to-front within the group). Empty list
  // = no valid clip target (e.g. adjustment is the bottom-most
  // sibling); the compositor falls back to unclipped behavior in
  // that case.
  const clipTargets = new Map<string, string[]>();
  for (const id in opts.document.layers) {
    const adj = opts.document.layers[id];
    if (adj.type !== 'adjustment' || !adj.clipToBelow || !adj.visible) continue;
    const leafIds = resolveClipTargetLeaves(opts.document, adj);
    if (leafIds.length > 0) clipTargets.set(adj.id, leafIds);
  }

  // Cache & lookahead bookkeeping.
  //   * `runningFp` is the fingerprint of "everything painted into
  //     composite so far that an adjustment has consumed". Each
  //     adjustment that runs (hit or miss) advances it.
  //   * `pendingLeafNodes` / `pendingLeafFps` defer leaf painting.
  //     A leaf only needs to actually hit composite when a
  //     subsequent cache-miss adjustment will read it via
  //     `getImageData`. If an adjustment above the leaf cache-hits,
  //     the hit's backing already encodes the leaf — pending entries
  //     are discarded without paint cost. This is the headline win
  //     on eye-toggles and adjustment edits: zero leaves drawn when
  //     the stack is fully cached.
  //   * `lastAdjPosition` lets us stop after the front-most
  //     adjustment. Leaves above it can't affect `composite` (which
  //     is only ever read as adjustment input) and get rendered by
  //     Fabric in their normal z-order regardless.
  let runningFp = '';
  const pendingLeafNodes: LayerNode[] = [];
  const pendingLeafFps: string[] = [];
  let lastAdjPosition = -1;
  for (let i = order.length - 1; i >= 0; i--) {
    if (order[i].type === 'adjustment' && order[i].visible) {
      lastAdjPosition = i;
      break;
    }
  }

  // Reusable scratch buffer for the interactive-mode proxy. Allocated
  // lazily on first cache-miss adjustment when interactiveMode is on
  // and the doc is larger than the proxy edge cap. A single buffer
  // is shared across every adjustment in the pass — they all use the
  // same dims (derived from canvas dims), so the alloc is cheap and
  // doesn't need to be per-adjustment. When the caller supplies an
  // `opts.proxyScratch` holder, the buffers persist across pass
  // boundaries too — avoids GC churn at 60fps interactive frames.
  const proxyScale =
    opts.interactiveMode === true
      ? Math.min(1, INTERACTIVE_PROXY_MAX_EDGE / Math.max(opts.width, opts.height))
      : 1;
  const useProxy = proxyScale < 1;
  let proxyBuffer: HTMLCanvasElement | null = null;
  let proxyResultBuffer: HTMLCanvasElement | null = null;

  for (let pos = 0; pos <= lastAdjPosition; pos++) {
    const node = order[pos];
    if (!node.visible) continue;
    if (node.type === 'group') continue;

    if (isCompositableLeaf(node)) {
      pendingLeafNodes.push(node);
      pendingLeafFps.push(leafFingerprint(node, pos));
      continue;
    }

    if (node.type === 'adjustment' && node.adjustment) {
      if (!isImplementedKind(node.adjustment.kind)) continue;
      const backing = opts.getAdjustmentBacking(node.id);
      if (!backing) continue;
      // Sizing mismatch is the caller's bug — the backing canvas
      // is created at the canvas dimensions and should stay that
      // way. Skip silently rather than producing wrong pixels.
      if (backing.width !== opts.width || backing.height !== opts.height) continue;

      const clipLeafIds = clipTargets.get(node.id) ?? [];
      // Build the input fingerprint by extending the running fp with
      // any pending (not-yet-painted) leaves. Pending leaves are part
      // of this adjustment's input even though we haven't paid for
      // their paint yet.
      let inputFp = runningFp;
      for (const fp of pendingLeafFps) inputFp += '|L:' + fp;
      const clipFp = clipLeafIds
        .map((lid) => leafFingerprint(opts.document.layers[lid], -1))
        .join('|');
      const adjFp = inputFp + '|A:' + adjustmentFingerprint(node, pos, clipFp);

      const cached = opts.cache?.get(node.id);
      if (cached && cached.fingerprint === adjFp) {
        // Cache hit. The backing already holds the correct
        // post-adjustment pixels — including every pending leaf's
        // contribution to its input. Discard pendings and rebase
        // composite to the cached output so subsequent leaves /
        // adjustments stack correctly.
        pendingLeafNodes.length = 0;
        pendingLeafFps.length = 0;
        compositeCtx.clearRect(0, 0, opts.width, opts.height);
        compositeCtx.drawImage(backing, 0, 0);
        runningFp = adjFp;
        cacheHits++;
        continue;
      }

      // Cache miss. Flush any deferred leaves into composite so the
      // adjustment sees the correct input.
      for (const pendingNode of pendingLeafNodes) {
        opts.paintLeaf(composite, pendingNode.id);
      }
      pendingLeafNodes.length = 0;
      pendingLeafFps.length = 0;

      let applied = false;
      if (useProxy) {
        // Interactive: downsample composite to proxy, shader on
        // proxy, upsample to backing. Cuts per-adjustment cost on
        // 4K from ~90ms to ~10-15ms at the price of a blurry
        // backing until the next non-interactive pass.
        if (!proxyBuffer) {
          const pw = Math.max(1, Math.round(opts.width * proxyScale));
          const ph = Math.max(1, Math.round(opts.height * proxyScale));
          const buffers = acquireProxyBuffers(opts.proxyScratch, pw, ph);
          proxyBuffer = buffers.composite;
          proxyResultBuffer = buffers.result;
        }
        applied = runAdjustmentAtProxy(
          composite,
          proxyBuffer,
          proxyResultBuffer!,
          backing,
          node.adjustment,
        );
      } else {
        const sourcePixels = compositeCtx.getImageData(0, 0, opts.width, opts.height);
        applied = applyAdjustmentToCanvas(sourcePixels, backing, node.adjustment);
      }
      if (!applied) continue;
      cacheMisses++;

      if (clipLeafIds.length > 0) {
        // Clipped adjustment: paint every leaf in the clip target
        // into a fresh mask canvas (their union alpha forms the
        // clip region for groups), then blend the adjusted result
        // with the pre-adjustment composite using that alpha.
        // Outside the clip region (alpha == 0) the source shows
        // through; inside, the full adjustment lands.
        //
        // Implementation uses canvas2d composite operations rather
        // than per-pixel JS. A 4K canvas's per-pixel blend was the
        // dominant cost in the slow-path (multi-second eye-toggle
        // lag); GPU-accelerated `destination-in` / `destination-out`
        // / `source-over` brings it down to a few drawImage calls.
        const clipMask = document.createElement('canvas');
        clipMask.width = opts.width;
        clipMask.height = opts.height;
        for (const lid of clipLeafIds) {
          opts.paintLeaf(clipMask, lid);
        }
        maskAdjustmentByAlphaComposite(backing, composite, clipMask);
      }

      // The adjustment's output is now the running composite for
      // any further adjustments above it.
      compositeCtx.clearRect(0, 0, opts.width, opts.height);
      compositeCtx.drawImage(backing, 0, 0);
      runningFp = adjFp;

      // Cache write policy: only store fingerprints that correspond
      // to FULL-resolution backings. Interactive passes write proxy-
      // res upsampled pixels to backings; setting the cache then
      // would let a future non-interactive pass with the same
      // fingerprint reuse blurry pixels. Delete instead so the next
      // non-interactive pass forces a fresh full-res render.
      if (opts.cache) {
        if (useProxy) opts.cache.delete(node.id);
        else opts.cache.set(node.id, { fingerprint: adjFp });
      }
    }
  }

  return { cacheHits, cacheMisses };
}

/** Resolve a pair of proxy buffers for the interactive path. When
 *  the caller supplied a `ProxyScratchHolder` whose canvases match
 *  the requested dims, we reuse them — no alloc, no GC. When the
 *  holder is missing, empty, or has stale dims, we allocate fresh
 *  and (if a holder exists) cache the new buffers in it for the
 *  next call. */
function acquireProxyBuffers(
  holder: ProxyScratchHolder | undefined,
  pw: number,
  ph: number,
): { composite: HTMLCanvasElement; result: HTMLCanvasElement } {
  if (
    holder &&
    holder.composite &&
    holder.result &&
    holder.composite.width === pw &&
    holder.composite.height === ph &&
    holder.result.width === pw &&
    holder.result.height === ph
  ) {
    return { composite: holder.composite, result: holder.result };
  }
  const composite = document.createElement('canvas');
  composite.width = pw;
  composite.height = ph;
  const result = document.createElement('canvas');
  result.width = pw;
  result.height = ph;
  if (holder) {
    holder.composite = composite;
    holder.result = result;
  }
  return { composite, result };
}

/** Build a stable fingerprint for a leaf node. Includes every field
 *  that influences what `paintLeaf` writes (visibility is handled
 *  outside — invisible leaves never reach here), plus the leaf's
 *  position in back-to-front order so reordering changes the
 *  fingerprint of every adjustment above the moved leaf.
 *
 *  Fabric-side state (transform matrix, paint-surface pixel content)
 *  is intentionally NOT in the fingerprint. The compositor only
 *  re-runs on `state.document` mutations; Fabric-only mutations
 *  don't trigger it today and the cache doesn't change that. See
 *  the "stale-adjustment-after-reload + paint" note in the project
 *  doc for the long-standing limitation this preserves. */
function leafFingerprint(node: LayerNode | undefined, position: number): string {
  if (!node) return '';
  return [
    position,
    node.id,
    node.opacity ?? 1,
    node.blendMode ?? 'source-over',
    node.effects ? JSON.stringify(node.effects) : '',
    node.mask ? JSON.stringify(node.mask) : '',
  ].join(':');
}

/** Build the per-adjustment fingerprint segment. Combined with the
 *  input fingerprint (running composite up to this point) to form
 *  the full cache key for this adjustment. */
function adjustmentFingerprint(node: LayerNode, position: number, clipFp: string): string {
  return [
    position,
    node.id,
    node.opacity ?? 1,
    node.blendMode ?? 'source-over',
    node.clipToBelow ? '1' : '0',
    JSON.stringify(node.adjustment ?? null),
    clipFp,
  ].join(':');
}

/** Run a single adjustment at viewport-resolution proxy. Downsamples
 *  the running composite to the proxy buffer via GPU `drawImage`,
 *  runs the GPU shader on the smaller texture, and upsamples the
 *  result into the full-resolution backing. Returns false on shader
 *  failure (caller falls through identical to the non-proxy path's
 *  `applied===false` skip).
 *
 *  The two proxy canvases (`proxyComposite` for the downsampled
 *  input, `proxyResult` as the shader output target) are shared
 *  across every adjustment in the pass — allocated once by the
 *  caller. */
function runAdjustmentAtProxy(
  composite: HTMLCanvasElement,
  proxyComposite: HTMLCanvasElement,
  proxyResult: HTMLCanvasElement,
  backing: HTMLCanvasElement,
  adjustment: NonNullable<LayerNode['adjustment']>,
): boolean {
  const pw = proxyComposite.width;
  const ph = proxyComposite.height;
  const inCtx = proxyComposite.getContext('2d', { willReadFrequently: true });
  if (!inCtx) return false;
  inCtx.clearRect(0, 0, pw, ph);
  inCtx.drawImage(composite, 0, 0, composite.width, composite.height, 0, 0, pw, ph);
  const sourcePixels = inCtx.getImageData(0, 0, pw, ph);
  const applied = applyAdjustmentToCanvas(sourcePixels, proxyResult, adjustment);
  if (!applied) return false;
  const outCtx = backing.getContext('2d');
  if (!outCtx) return false;
  outCtx.clearRect(0, 0, backing.width, backing.height);
  // `'low'` smoothing is the right knob for live preview — 'high'
  // is multi-pass lanczos under the hood and stretches a 4K
  // upsample into the tens-of-ms range, defeating the proxy savings.
  outCtx.imageSmoothingEnabled = true;
  outCtx.imageSmoothingQuality = 'low';
  outCtx.drawImage(proxyResult, 0, 0, pw, ph, 0, 0, backing.width, backing.height);
  return true;
}

/** Resolve a clipped adjustment's mask source.
 *
 *  Walks the adjustment's siblings (front-most first) past itself
 *  to find the next visible non-adjustment sibling — that's the
 *  clip target. If the target is a single rasterizable leaf, the
 *  result is `[targetId]`. If the target is a group, the result is
 *  the back-to-front-ordered ids of every visible `image`/`paint`
 *  descendant — the compositor paints all of them into one mask
 *  canvas to form the union alpha.
 *
 *  Returns an empty array when no valid clip target exists (e.g.
 *  the adjustment is the bottom-most sibling). Callers MUST treat
 *  empty as "fall back to unclipped" rather than "clip to zero
 *  alpha" — otherwise the adjustment would silently vanish. */
function resolveClipTargetLeaves(doc: EditorDocument, adj: LayerNode): string[] {
  const parentId = adj.parentId;
  const siblings = parentId === null ? doc.rootIds : (doc.layers[parentId]?.childIds ?? []);
  const idx = siblings.indexOf(adj.id);
  if (idx < 0) return [];
  // Front-most-first ordering: siblings AFTER the adjustment are
  // BELOW it visually. Walk forward to find the next valid clip
  // sibling.
  let targetId: string | null = null;
  for (let i = idx + 1; i < siblings.length; i++) {
    const sib = doc.layers[siblings[i]];
    if (!sib || !sib.visible) continue;
    if (sib.type === 'adjustment') continue;
    targetId = sib.id;
    break;
  }
  if (!targetId) return [];
  const target = doc.layers[targetId];
  if (!target) return [];
  if (target.type === 'image' || target.type === 'paint') return [targetId];
  if (target.type === 'group') {
    // `traverseDescendants` yields the starting node first (the
    // group), then descendants front-most-first. We filter to just
    // visible rasterizable leaves; the iteration order doesn't
    // matter for the mask (alpha union is associative).
    const out: string[] = [];
    for (const descendant of traverseDescendants(doc, targetId)) {
      if (!descendant.visible) continue;
      if (descendant.type === 'image' || descendant.type === 'paint') {
        out.push(descendant.id);
      }
    }
    return out;
  }
  // text/shape/empty/nested-adjustment as the immediate sibling:
  // no rasterizable surface to clip against. Treat as no clip
  // target — adjustment runs unclipped against the running
  // composite.
  return [];
}

/** Blend the adjusted pixels in `backing` with `sourceCanvas` using
 *  `clipMask`'s alpha as the per-pixel mix weight. Replaces
 *  `backing`'s pixels in place. Result is mathematically the lerp
 *  `out = source * (1 - mask.alpha) + adjusted * mask.alpha`.
 *
 *  Uses canvas2d composite operations exclusively — no per-pixel
 *  JavaScript. `destination-in` keeps the adjusted pixels where the
 *  mask is opaque; `destination-out` punches the source where the
 *  mask is opaque; `source-over` recombines them. Each step is one
 *  drawImage call that the browser dispatches to the GPU when
 *  possible, so on a 4K canvas this completes in single-digit ms
 *  versus hundreds of ms for the per-pixel JS loop it replaced.
 *
 *  All three inputs must be the same size; sizing checks are the
 *  caller's responsibility. */
function maskAdjustmentByAlphaComposite(
  backing: HTMLCanvasElement,
  sourceCanvas: HTMLCanvasElement,
  clipMask: HTMLCanvasElement,
): void {
  const w = backing.width;
  const h = backing.height;
  // Snapshot the adjusted pixels — `backing` is about to be
  // rewritten and we need the pre-rewrite pixels to recombine.
  const adjustedTemp = document.createElement('canvas');
  adjustedTemp.width = w;
  adjustedTemp.height = h;
  const adjCtx = adjustedTemp.getContext('2d');
  if (!adjCtx) return;
  adjCtx.drawImage(backing, 0, 0);
  // Keep only the part of `adjustedTemp` inside the clip region.
  adjCtx.globalCompositeOperation = 'destination-in';
  adjCtx.drawImage(clipMask, 0, 0);

  // Build a sourceTemp that contains source pixels OUTSIDE the
  // clip region (source with the clip region punched out).
  const sourceTemp = document.createElement('canvas');
  sourceTemp.width = w;
  sourceTemp.height = h;
  const srcCtx = sourceTemp.getContext('2d');
  if (!srcCtx) return;
  srcCtx.drawImage(sourceCanvas, 0, 0);
  srcCtx.globalCompositeOperation = 'destination-out';
  srcCtx.drawImage(clipMask, 0, 0);

  // Recompose into `backing`. `sourceTemp` covers outside-clip,
  // `adjustedTemp` covers inside-clip — they tile together with
  // straight `source-over` since their alpha regions don't overlap.
  const ctx = backing.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(sourceTemp, 0, 0);
  ctx.drawImage(adjustedTemp, 0, 0);
}

/** Flatten the tree depth-first and reverse for back-to-front
 *  paint order. Inlined rather than importing `toOrderedList` +
 *  `.reverse()` so the slow-path costs one less allocation and
 *  the traversal stays explicit at the call site. */
function backToFrontOrder(doc: EditorDocument): LayerNode[] {
  const front: LayerNode[] = [];
  const stack: string[] = [];
  for (let i = doc.rootIds.length - 1; i >= 0; i--) {
    stack.push(doc.rootIds[i]);
  }
  while (stack.length > 0) {
    const id = stack.pop()!;
    const node = doc.layers[id];
    if (!node) continue;
    front.push(node);
    if (node.type === 'group' && node.childIds && node.childIds.length > 0) {
      for (let i = node.childIds.length - 1; i >= 0; i--) {
        stack.push(node.childIds[i]);
      }
    }
  }
  // `front` is front-most-first (same as `toOrderedList`); reverse
  // for back-to-front paint order.
  return front.reverse();
}
